import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Gift, Users, TrendingUp, ChevronRight, HelpCircle, Sparkles } from "lucide-react";

export const ReferralsInfo = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Referral Program</h1>
        </div>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Earn rewards when you refer friends to Nordex</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How It Works</h2>
          <p className="text-muted-foreground mb-6">
            The Nordex Referral Program rewards both you and your friends. When someone signs up using your 
            referral link and starts trading, you both earn rewards based on their trading activity.
          </p>

          <div className="grid gap-4 md:grid-cols-2 mt-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">You Earn 10%</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                As the referrer, you earn <strong className="text-foreground">10% of the trading fees</strong> paid 
                by your referred friends. This is paid continuously as they trade on the platform.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Gift className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">They Get 10% Off</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Your referred friends receive a <strong className="text-foreground">10% discount</strong> on all 
                trading fees, making trading on Nordex more affordable for them.
              </p>
            </div>
          </div>
        </section>

        {/* Example Calculation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Example</h2>
          
          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Referral Earnings Breakdown</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Your friend generates trading fees:</span>
                <span className="font-semibold">$100</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Their discount (10%):</span>
                <span className="font-semibold text-primary">-$10</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">They pay:</span>
                <span className="font-semibold">$90</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold">Your reward (10% of original $100):</span>
                <span className="font-bold text-primary text-lg">$10</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Both you and your friend benefit from every trade they make!
            </p>
          </div>
        </section>

        {/* Getting Started */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Getting Started</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Get Your Referral Link</h3>
                <p className="text-sm text-muted-foreground">
                  Log in to your Nordex account and navigate to the Referrals page. You'll find your unique 
                  referral link there. You can also create a custom vanity URL like nordex.trade/join/yourname.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Share with Friends</h3>
                <p className="text-sm text-muted-foreground">
                  Share your referral link with friends, family, or your community. They can be on social media, 
                  Discord, Telegram, or any platform where you interact with potential traders.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">They Sign Up & Trade</h3>
                <p className="text-sm text-muted-foreground">
                  When someone signs up using your link and starts trading, they'll automatically receive their 
                  10% discount, and you'll start earning 10% on their trading fees.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Track Your Earnings</h3>
                <p className="text-sm text-muted-foreground">
                  View all your referrals and earnings in real-time on your Referrals dashboard. You can see 
                  who's trading, how much they've generated, and your total earnings.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <h3 className="font-semibold mb-1">Withdraw Rewards</h3>
                <p className="text-sm text-muted-foreground">
                  Your referral earnings accumulate in your account balance automatically. You can withdraw 
                  them to your wallet at any time, just like your trading profits.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Program Benefits</h2>
          
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Lifetime Earnings:</span>
                <span className="text-sm text-muted-foreground"> Continue earning as long as your referrals are trading</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">No Limits:</span>
                <span className="text-sm text-muted-foreground"> Refer unlimited friends and earn from all of them</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Automatic Tracking:</span>
                <span className="text-sm text-muted-foreground"> All referrals and earnings are tracked automatically</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Instant Credits:</span>
                <span className="text-sm text-muted-foreground"> Earnings are credited to your account immediately after each trade</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Win-Win:</span>
                <span className="text-sm text-muted-foreground"> Both you and your friends benefit from every trade</span>
              </div>
            </li>
          </ul>
        </section>

        {/* Affiliate vs Referral */}
        <section className="mb-12">
          <div className="p-6 border border-primary/20 rounded-lg bg-primary/5">
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Looking for Higher Commissions?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  If you're a content creator, influencer, or have a larger audience, check out our Affiliate Program. 
                  Affiliates can earn higher commission rates and additional revenue share bonuses.
                </p>
                <a
                  href="/affiliate-info"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                >
                  Learn about the Affiliate Program
                  <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Get Started CTA */}
        <section>
          <div className="p-8 border border-border rounded-xl bg-gradient-to-br from-primary/5 to-primary/10">
            <h2 className="text-2xl font-semibold mb-3">Start Referring Today</h2>
            <p className="text-muted-foreground mb-6">
              Share Nordex with your friends and start earning rewards on their trading activity. 
              It's a win-win for everyone involved!
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/referrals"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                <Users className="h-5 w-5" />
                Get Your Referral Link
              </a>
              <a
                href="/support"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border rounded-lg font-semibold hover:bg-accent transition-colors"
              >
                <HelpCircle className="h-5 w-5" />
                Contact Support
              </a>
            </div>
          </div>
        </section>

        {/* Navigation */}
        <DocsNavigation
          previous={{ title: "Support & FAQ", href: "/support" }}
          next={{ title: "Affiliate Program", href: "/affiliate-info" }}
        />
      </div>
    </DocsLayout>
  );
};

